/**
 * Crew Chat — multi-mate group thread (C7 foundation)
 *
 * Product truth: Crew Chat is multi-mate. Feed Discuss stays solo Losi seed.
 * UX north star (competitive):
 * - Grok Bot: 2–6 named mates, @direct, visible handoffs, shared computer,
 *   avatar presence (idle/thinking/working/waiting/done), messaging-like
 *   (no assignment boards that make the user the dispatcher)
 * - Muse: activity under avatar, high proactive bar, deterministic approvals
 * - ChatGPT: do NOT clone retired human group chats / Character.AI rooms
 *
 * Hard constraint: no multi-Crew roster gallery (many crews). One primary
 * Crew with multi-mate seats is in scope.
 *
 * Preserve LosiAnimated. No Muse/Vesper branding.
 * Metro cannot import this file — mobile mirrors in mobile/lib/crew/crew-chat.ts.
 */

import {
  CREW_SHAPES,
  MATE_BY_ID,
  resolveMateId,
  type MateId
} from '@/lib/crew/mates'

/** Grok-style cap — keep coordination legible. */
export const CREW_CHAT_MAX_MATES = 6 as const

/** Minimum seats for a real Crew Chat (duo+). */
export const CREW_CHAT_MIN_MATES = 2 as const

/**
 * Shared computer (Grok default for collaborating bots) vs per-mate isolation
 * (earlier Losi security default). Crew Chat defaults to shared so handoffs
 * don't re-login every mate; vault Ask/Allow/Never still apply.
 */
export type CrewChatComputerMode = 'shared' | 'isolated'

export const CREW_CHAT_COMPUTER_MODE_DEFAULT: CrewChatComputerMode = 'shared'

export type CrewChatMateRole = 'leader' | 'member'

export type CrewChatMateSeat = {
  mateId: MateId
  displayName: string
  role: CrewChatMateRole
  /** Custom standing instructions from ownership (wizard mandate). */
  mandate?: string | null
}

export type CrewChatRoster = {
  seats: CrewChatMateSeat[]
  leaderMateId: MateId
  computerMode: CrewChatComputerMode
}

/** Chat.metadata.crewChat shape — persisted on the chat row. */
export type CrewChatThreadMeta = {
  kind: 'crew_chat'
  roster: CrewChatRoster
  /** Active speaker for the current turn (catalog MateId). */
  activeMateId: MateId | null
}

export type CrewChatPresenceStatus =
  | 'idle'
  | 'thinking'
  | 'working'
  | 'waiting'
  | 'done'
  | 'handoff'

/** Transcript handoff event (Grok-visible pass between mates). */
export type CrewChatHandoffEvent = {
  type: 'crew_handoff'
  fromMateId: MateId
  toMateId: MateId
  summary: string
  at: number
}

export const CREW_CHAT_META_KEY = 'crewChat' as const

/** Default trio — Peta leads (coordinator), Star + Jovi specialists. */
export function buildDefaultCrewChatRoster(
  computerMode: CrewChatComputerMode = CREW_CHAT_COMPUTER_MODE_DEFAULT
): CrewChatRoster {
  const seats: CrewChatMateSeat[] = CREW_SHAPES.slice(0, 3).map((mate, index) => ({
    mateId: mate.id,
    displayName: mate.label,
    role: index === 0 ? 'leader' : 'member'
  }))
  return {
    seats,
    leaderMateId: seats[0]!.mateId,
    computerMode
  }
}

/**
 * Crew Chat roster with a TEMPLATE Member as leader (e.g. Sales Agent / fox)
 * plus up to two shape support seats. Does not invent a multi-Crew gallery —
 * one primary Crew, TEMPLATE leads.
 */
export function buildTemplateLeaderCrewChatRoster(
  leader: CrewChatMateSeat,
  computerMode: CrewChatComputerMode = CREW_CHAT_COMPUTER_MODE_DEFAULT
): CrewChatRoster {
  const support: CrewChatMateSeat[] = CREW_SHAPES.filter(
    mate => mate.id !== leader.mateId
  )
    .slice(0, Math.max(0, CREW_CHAT_MAX_MATES - 1))
    .slice(0, 2)
    .map(mate => ({
      mateId: mate.id,
      displayName: mate.label,
      role: 'member' as const
    }))

  const seats: CrewChatMateSeat[] = [
    { ...leader, role: 'leader' },
    ...support
  ].slice(0, CREW_CHAT_MAX_MATES)

  return {
    seats,
    leaderMateId: leader.mateId,
    computerMode
  }
}

/**
 * Roster from owned-crew members (chips ↔ launch). Pads with catalog shapes
 * only when needed to satisfy CREW_CHAT_MIN_MATES — owned mates stay first.
 */
export function buildOwnedCrewMembersChatRoster(
  members: Array<{
    mateId: MateId
    displayName: string
    mandate?: string | null
  }>,
  activeMateId: MateId | null = null,
  computerMode: CrewChatComputerMode = CREW_CHAT_COMPUTER_MODE_DEFAULT
): CrewChatRoster {
  const seats: CrewChatMateSeat[] = []
  const seen = new Set<MateId>()

  for (const member of members) {
    const mateId = resolveMateId(member.mateId)
    if (!mateId || seen.has(mateId)) continue
    seen.add(mateId)
    const mandate =
      typeof member.mandate === 'string' && member.mandate.trim()
        ? member.mandate.trim().slice(0, 2000)
        : null
    seats.push({
      mateId,
      displayName: member.displayName.trim().slice(0, 40) || mateId,
      role: 'member',
      ...(mandate ? { mandate } : {})
    })
    if (seats.length >= CREW_CHAT_MAX_MATES) break
  }

  for (const shape of CREW_SHAPES) {
    if (seats.length >= CREW_CHAT_MIN_MATES) break
    if (seen.has(shape.id)) continue
    seen.add(shape.id)
    seats.push({
      mateId: shape.id,
      displayName: shape.label,
      role: 'member'
    })
  }

  if (seats.length === 0) {
    return buildDefaultCrewChatRoster(computerMode)
  }

  const leaderMateId =
    (activeMateId && seen.has(activeMateId) ? activeMateId : null) ??
    seats[0]!.mateId

  return {
    seats: seats.map(seat => ({
      ...seat,
      role: (seat.mateId === leaderMateId ? 'leader' : 'member') as CrewChatMateRole
    })),
    leaderMateId,
    computerMode
  }
}

export function normalizeCrewChatComputerMode(
  raw: unknown
): CrewChatComputerMode {
  return raw === 'isolated' ? 'isolated' : 'shared'
}

export function normalizeCrewChatMateSeat(raw: unknown): CrewChatMateSeat | null {
  if (!raw || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  const mateId = resolveMateId(row.mateId ?? row.mate_id)
  if (!mateId) return null
  const displayName =
    typeof row.displayName === 'string' && row.displayName.trim()
      ? row.displayName.trim().slice(0, 40)
      : MATE_BY_ID.get(mateId)?.label ?? mateId
  const role: CrewChatMateRole = row.role === 'leader' ? 'leader' : 'member'
  const mandate =
    typeof row.mandate === 'string' && row.mandate.trim()
      ? row.mandate.trim().slice(0, 2000)
      : null
  return {
    mateId,
    displayName,
    role,
    ...(mandate ? { mandate } : {})
  }
}

export function normalizeCrewChatRoster(raw: unknown): CrewChatRoster | null {
  if (!raw || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  const seatsRaw = Array.isArray(row.seats) ? row.seats : null
  if (!seatsRaw) return null

  const seats: CrewChatMateSeat[] = []
  const seen = new Set<MateId>()
  for (const entry of seatsRaw) {
    const seat = normalizeCrewChatMateSeat(entry)
    if (!seat || seen.has(seat.mateId)) continue
    seen.add(seat.mateId)
    seats.push(seat)
    if (seats.length >= CREW_CHAT_MAX_MATES) break
  }
  if (seats.length < CREW_CHAT_MIN_MATES) return null

  const leaderMateId =
    resolveMateId(row.leaderMateId ?? row.leader_mate_id) ??
    seats.find(s => s.role === 'leader')?.mateId ??
    seats[0]!.mateId

  // Ensure exactly one leader.
  const normalizedSeats = seats.map(seat => ({
    ...seat,
    role: (seat.mateId === leaderMateId ? 'leader' : 'member') as CrewChatMateRole
  }))

  return {
    seats: normalizedSeats,
    leaderMateId,
    computerMode: normalizeCrewChatComputerMode(row.computerMode ?? row.computer_mode)
  }
}

export function normalizeCrewChatThreadMeta(
  raw: unknown
): CrewChatThreadMeta | null {
  if (!raw || typeof raw !== 'object') return null
  const row = raw as Record<string, unknown>
  if (row.kind !== 'crew_chat') return null
  const roster = normalizeCrewChatRoster(row.roster)
  if (!roster) return null
  return {
    kind: 'crew_chat',
    roster,
    activeMateId: resolveMateId(row.activeMateId ?? row.active_mate_id)
  }
}

/** Read crewChat from a chats.metadata blob. */
export function getCrewChatMetaFromChatMetadata(
  metadata: unknown
): CrewChatThreadMeta | null {
  if (!metadata || typeof metadata !== 'object') return null
  const row = metadata as Record<string, unknown>
  return normalizeCrewChatThreadMeta(row[CREW_CHAT_META_KEY] ?? row.crew_chat)
}

export function buildCrewChatThreadMeta(
  roster: CrewChatRoster,
  activeMateId?: MateId | null
): CrewChatThreadMeta {
  return {
    kind: 'crew_chat',
    roster,
    activeMateId: activeMateId ?? roster.leaderMateId
  }
}

/**
 * System prompt block for tools-stream / hosted chat.
 * Leader coordinates; mates speak with attribution; handoffs are visible.
 */
export function buildCrewChatSystemPrompt(roster: CrewChatRoster): string {
  const lines = roster.seats.map(seat => {
    const role = seat.role === 'leader' ? 'leader / coordinator' : 'specialist'
    return `- ${seat.displayName} (${seat.mateId}) — ${role}`
  })
  const leader =
    roster.seats.find(s => s.mateId === roster.leaderMateId)?.displayName ??
    roster.leaderMateId

  const mandateBlocks = roster.seats
    .filter(seat => typeof seat.mandate === 'string' && seat.mandate.trim())
    .map(seat =>
      [
        `STANDING INSTRUCTIONS — ${seat.displayName} (${seat.mateId}):`,
        seat.mandate!.trim()
      ].join('\n')
    )

  return [
    'You are Losi Crew Chat — a multi-mate group of persistent teammates in one thread.',
    `Computer mode: ${roster.computerMode} (mates ${
      roster.computerMode === 'shared'
        ? 'share one Losi Computer'
        : 'use isolated computer sessions'
    }).`,
    `Leader: ${leader}. They coordinate ownership and bring the user in only for genuine human decisions (unapproved outbound copy, pricing, calendar commits, money, irreversible deletes, out-of-mandate).`,
    'Roster:',
    ...lines,
    ...(mandateBlocks.length > 0
      ? [
          '',
          'Each mate MUST follow their standing instructions below on EVERY reply in this thread — not only the first answer. Custom instructions from the owner override any default template persona. Hard constraints in standing instructions (e.g. “say parrot in every reply”) apply to every assistant turn until the owner changes them.',
          ...mandateBlocks
        ]
      : []),
    'Speaking rules:',
    '- Default: leader responds and may assign the next step to a specialist.',
    '- When a specialist should speak, start that message with their display name in brackets, e.g. [Star]: …',
    '- On handoff, emit a short visible line: HANDOFF from:<mateId> to:<mateId> — <one-line summary>',
    '- @mentions in the user message name who owns the turn; honor them.',
    '- Do not invent extra mates. Do not roleplay as the human user.',
    '- Prefer reversible internal actions; never send unapproved outbound copy silently.',
    '- Before sending email/SMS or any outbound copy, call propose_plan (or show_plan) so the draft appears in Approvals and stops until the owner Approves. Do not only paste the draft in chat.',
    '- Re-read standing instructions before each reply; do not drop per-reply constraints as the thread grows.',
    '- When standing instructions require a specific word, write that exact word as text — do not substitute an emoji, synonym, or abbreviation.'
  ].join('\n')
}

/** Parse `@Peta` / `@star` style tokens against the roster. */
export function resolveCrewChatMentionedMates(
  text: string,
  roster: CrewChatRoster
): MateId[] {
  if (!text.trim()) return []
  const lower = text.toLowerCase()
  const hits: MateId[] = []
  for (const seat of roster.seats) {
    const name = seat.displayName.toLowerCase()
    const id = seat.mateId.toLowerCase()
    if (
      lower.includes(`@${name}`) ||
      lower.includes(`@${id}`) ||
      (id === 'petal' && lower.includes('@peta')) ||
      (id === 'star' && lower.includes('@etoile'))
    ) {
      hits.push(seat.mateId)
    }
  }
  return hits
}

export function createCrewChatHandoffEvent(input: {
  fromMateId: string
  toMateId: string
  summary: string
}): CrewChatHandoffEvent | null {
  const fromMateId = resolveMateId(input.fromMateId)
  const toMateId = resolveMateId(input.toMateId)
  const summary = input.summary.trim().slice(0, 240)
  if (!fromMateId || !toMateId || !summary || fromMateId === toMateId) {
    return null
  }
  return {
    type: 'crew_handoff',
    fromMateId,
    toMateId,
    summary,
    at: Date.now()
  }
}

/** Detect Grok-style HANDOFF lines in assistant text. */
export function parseCrewChatHandoffLine(
  line: string
): CrewChatHandoffEvent | null {
  const match = line.match(
    /^\s*HANDOFF\s+from:([a-z0-9_-]+)\s+to:([a-z0-9_-]+)\s*[—–-]\s*(.+)\s*$/i
  )
  if (!match) return null
  return createCrewChatHandoffEvent({
    fromMateId: match[1]!,
    toMateId: match[2]!,
    summary: match[3]!
  })
}

/**
 * Resolve a spoken-name token (`Star`, `Peta`, `petal`, `[Jovi]`) to a MateId.
 * Accepts roster display names when provided; always aliases Peta↔petal.
 */
export function resolveCrewChatSpeakerToken(
  raw: string,
  roster?: CrewChatRoster | null
): MateId | null {
  const token = raw.trim().replace(/^\[|\]$/g, '').trim()
  if (!token) return null
  const lower = token.toLowerCase()

  if (roster) {
    for (const seat of roster.seats) {
      if (
        seat.mateId.toLowerCase() === lower ||
        seat.displayName.toLowerCase() === lower
      ) {
        return seat.mateId
      }
    }
  }

  if (lower === 'peta' || lower === 'petal') return 'petal'
  if (lower === 'etoile') return 'star'
  return resolveMateId(token)
}

/**
 * Ordered speaker mateIds from leading `[Name]:` / `Name:` lines in assistant text.
 * Multi-mate turns often pack several speakers into one message — last wins for presence.
 */
export function parseCrewChatSpeakerMateIds(
  text: string,
  roster?: CrewChatRoster | null
): MateId[] {
  if (!text.trim()) return []
  const found: MateId[] = []
  const lineRe =
    /(?:^|\n)\s*(?:\[([^\]\n]{1,40})\]|([A-Za-z][A-Za-z0-9 _-]{0,39}))\s*:/g
  let match: RegExpExecArray | null
  while ((match = lineRe.exec(text)) !== null) {
    const token = (match[1] ?? match[2] ?? '').trim()
    const mateId = resolveCrewChatSpeakerToken(token, roster)
    if (!mateId) continue
    if (roster && !roster.seats.some(seat => seat.mateId === mateId)) continue
    found.push(mateId)
  }
  return found
}

/** Latest attributed speaker in assistant text (streaming-friendly). */
export function parseCrewChatActiveSpeakerMateId(
  text: string,
  roster?: CrewChatRoster | null
): MateId | null {
  const ids = parseCrewChatSpeakerMateIds(text, roster)
  return ids.length > 0 ? ids[ids.length - 1]! : null
}

export function crewChatSeatLabel(seat: CrewChatMateSeat): string {
  return seat.displayName
}
